import React, { FunctionComponent } from "react";
import { Link } from "react-router-dom";
import YaraStringMatchRulesetFilter from "./YaraStringMatchRulesetFilter";
import YaraStringMatchRuleFilter from "./YaraStringMatchRuleFilter";
import YaraStringMatchVariableFilter from "./YaraStringMatchVariableFilter";
import ClearButton from "../../../search/components/ClearButton";
import OpenRulesetButton from "./OpenRulesetButton";
import YaraStringMatchPaging from "./YaraStringMatchPaging";
import { MenuItem, MenuItems } from "../../../../components/@types";
import { RenderWhen, TooltipIcon } from "../../../../components";
import { pathToSS } from "../../util";
import { SECTION_PATH } from "../../../../path";

interface IYaraStringMatchControlsProps {
  selectedRulesetName?: MenuItem;
  selectedRulesetId?: number | null;
  selectedRuleName?: MenuItem;
  selectedVariableName?: MenuItem;
  currentHighlight: { currentIndex: number };
  totalMatchesToHighlight?: number;
  setSelectedRulesetName: (value?: MenuItem) => void;
  setSelectedRuleName: (value?: MenuItem) => void;
  setSelectedVariableName: (value?: MenuItem) => void;
  setCurrentHighlight: (value: { currentIndex: number }) => void;
  rulesetOptions: MenuItems;
  ruleOptions: MenuItems;
  variableOptions: MenuItems;
  showOpenYaraRulesetButton?: boolean;
  hasExtractedFilesWithYaraMatches?: boolean;
  hash: string;
  sourceID?: number;
  relationshipId?: number;
}

const YaraStringMatchControls: FunctionComponent<
  IYaraStringMatchControlsProps
> = ({
  selectedRulesetName,
  selectedRulesetId,
  selectedRuleName,
  selectedVariableName,
  currentHighlight,
  totalMatchesToHighlight,
  setSelectedRulesetName,
  setSelectedRuleName,
  setSelectedVariableName,
  setCurrentHighlight,
  rulesetOptions,
  ruleOptions,
  variableOptions,
  showOpenYaraRulesetButton = true,
  hasExtractedFilesWithYaraMatches,
  hash,
  sourceID,
  relationshipId
}) => (
  <div>
    <div className="d-flex gap-5 pt-20">
      <span className="fw-bold">Filter YARA Matches</span>
      <TooltipIcon
        icon="info-circle"
        className="file-visualization__filter-tooltip"
        message="Only Yara Rulesets, Rules and Strings matched for this sample will be shown in the dropdowns."
      />
    </div>
    {rulesetOptions.length > 0 && (
      <div className="d-flex align-items-end flex-wrap gap-10 py-10">
        <YaraStringMatchRulesetFilter
          active={selectedRulesetName}
          items={rulesetOptions}
          onChange={setSelectedRulesetName}
        />

        <YaraStringMatchRuleFilter
          active={selectedRuleName}
          items={ruleOptions}
          disabled={selectedRulesetName == null}
          onChange={setSelectedRuleName}
        />

        <YaraStringMatchVariableFilter
          active={selectedVariableName}
          items={variableOptions}
          disabled={selectedRuleName == null}
          onChange={setSelectedVariableName}
        />

        {selectedRulesetName != null && (
          <>
            <ClearButton
              label="Clear Filters"
              onClick={() => {
                setSelectedRulesetName(undefined);
                setSelectedRuleName(undefined);
                setSelectedVariableName(undefined);
              }}
              disabled={
                !selectedRulesetName &&
                !selectedRuleName &&
                !selectedVariableName
              }
            />

            <RenderWhen condition={showOpenYaraRulesetButton}>
              <OpenRulesetButton selectedRulesetId={selectedRulesetId} />
            </RenderWhen>

            <YaraStringMatchPaging
              currentResult={currentHighlight.currentIndex}
              totalResults={totalMatchesToHighlight}
              onChangeCurrentResult={currentIndex =>
                setCurrentHighlight({ currentIndex })
              }
            />
          </>
        )}
      </div>
    )}
    {rulesetOptions.length === 0 &&
      hasExtractedFilesWithYaraMatches &&
      hash &&
      sourceID && (
        <div className={"pt-5"}>
          No YARA matched strings on the current file. Please check the{" "}
          <Link
            to={pathToSS(
              hash,
              sourceID,
              SECTION_PATH.EXTRACTED_FILES,
              relationshipId
            )}
            style={{ textDecoration: "underline" }}
          >
            extracted files
          </Link>{" "}
          for YARA matched strings.
        </div>
      )}
    {rulesetOptions.length === 0 && !hasExtractedFilesWithYaraMatches && (
      <div className={"pt-5"}>
        No YARA matched strings on the current file nor its extracted files.
      </div>
    )}
    {rulesetOptions.length > 0 &&
      hasExtractedFilesWithYaraMatches &&
      hash &&
      sourceID && (
        <div className={"pt-5"}>
          Check out{" "}
          <Link
            to={pathToSS(
              hash,
              sourceID,
              SECTION_PATH.EXTRACTED_FILES,
              relationshipId
            )}
            style={{ textDecoration: "underline" }}
          >
            extracted files
          </Link>{" "}
          for more matched strings.
        </div>
      )}
  </div>
);

export default YaraStringMatchControls;
